import { Marker } from "@react-google-maps/api";
import { Clusterer } from "@react-google-maps/marker-clusterer";
import React from "react";
import ReactGA from "react-ga";
import { useDispatch, useSelector } from "react-redux";
import {
  getInfoWindowState,
  setZoom,
  toggleInfoWindow
} from "../../store/actions";
import { setCenter } from "../../store/mapStore";
import {
  selectRestaurantOfferState,
  selectRestaurantStartingTimeString
} from "../../store/restaurantsStore/offerSelectors";
import { OFFER_LATER } from "../../types/offerStates";
import { getMarker } from "./getMarker";
import MarkerInfoWindow from "./MarkerInfoWindow";

const useMarker = (): [any, (marker: google.maps.Marker) => void] => {
  const [marker, setMarker] = React.useState<google.maps.Marker | null>(null);
  const markerLoadHandler = (marker: google.maps.Marker) => setMarker(marker);
  return [marker, markerLoadHandler];
};

const MarkerWithInfoWindow = ({
  restaurant,
  clusterer
}: {
  restaurant: RestaurantFromAPI;

  clusterer?: Clusterer;
}) => {
  const dispatch = useDispatch();
  const isOpen = useSelector(getInfoWindowState(restaurant.key));
  const offerState = useSelector(selectRestaurantOfferState(restaurant._id));
  const startingTimeString = useSelector(
    selectRestaurantStartingTimeString(restaurant._id)
  );
  const markerStartingTimeString =
    offerState === OFFER_LATER ? startingTimeString : undefined;

  const onToggleOpen = React.useCallback(() => {
    dispatch(setZoom(18));
    dispatch(toggleInfoWindow({ key: restaurant.key }));
    dispatch(setCenter({ lat: restaurant.lat, lng: restaurant.lng }));
    ReactGA.event({
      category: "Map",
      action: "Click Marker",
      label: restaurant.key
    });
  }, [dispatch, restaurant]);
  const onClose = React.useCallback(() => {
    dispatch(toggleInfoWindow({ key: restaurant.key, open: false }));
  }, [dispatch, restaurant.key]);
  const position = React.useMemo(
    () => ({
      lat: restaurant.lat,
      lng: restaurant.lng
    }),
    [restaurant.lat, restaurant.lng]
  );
  const icon = React.useMemo(() => getMarker(offerState), [offerState]);
  const [marker, markerLoadHandler] = useMarker();
  return (
    <Marker
      position={position}
      clusterer={clusterer}
      icon={icon}
      onLoad={markerLoadHandler}
      onClick={onToggleOpen}
      label={markerStartingTimeString}
    >
      {isOpen && marker && (
        <MarkerInfoWindow
          marker={marker}
          restaurant={restaurant}
          onClose={onClose}
        />
      )}
    </Marker>
  );
};

export default MarkerWithInfoWindow;
